import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { Suspense, useCallback } from "react";
import classNames from "classnames";
import { Icon, Tag, Text } from "@zapier/design-system";
import { Modal, Button } from "@zapier/design-system-beta";
import {
  clearCanvasAtom,
  hasZslAtom,
  showCanvasTemplateWidgetAtom,
  showAIStreamCreateModalAtom,
  emitEventOnAICopilotStartAtom,
  dismissFeedbackFormCommand,
  emitEventOnStartFromScratchAtom,
  emitAiPredictionUserFeedbackEventAtom,
  zslFeedbackPredictionIdAtom,
} from "@zapier/canvas-state";
import { Node, useReactFlow } from "@xyflow/react";

import { showWelcomeModalAtom } from "../../atoms/showWelcomeModalAtom";

import styles from "./WelcomeModalContent.module.css";
import { IMPLICIT_BAD } from "@zapier/canvas-constants";
import { onboardingDataAsyncAtom } from "../../atoms/onboardingDataAsyncAtom";

type Props = { handleCloseModal: () => void };

function SuspendedOnboardingDataPreloader() {
  useAtomValue(onboardingDataAsyncAtom);
  return null;
}
function OnboardingDataPreloader() {
  // We need onboarding data to load before hitting the next screen for a better UX
  // Loading at this screen so it's cached by the time it hits the next screen
  return (
    <Suspense fallback={null}>
      <SuspendedOnboardingDataPreloader />
    </Suspense>
  );
}

export function WelcomeModalContent({ handleCloseModal }: Props) {
  const setShowCanvasTemplateWidget = useSetAtom(showCanvasTemplateWidgetAtom);
  const setShowAIStreamCreateModal = useSetAtom(showAIStreamCreateModalAtom);
  const emitEventOnAICopilotStart = useSetAtom(emitEventOnAICopilotStartAtom);
  const clearCanvas = useSetAtom(clearCanvasAtom);
  const { centerAndZoomOnNode } = useCenterAndZoomOnNode();
  const hasZsl = useAtomValue(hasZslAtom);
  const setShowWelcomeModal = useSetAtom(showWelcomeModalAtom);
  const emitEventOnStartFromScratch = useSetAtom(
    emitEventOnStartFromScratchAtom,
  );
  const emitAiPredictionUserFeedbackEvent = useSetAtom(
    emitAiPredictionUserFeedbackEventAtom,
  );
  const prediction_id = useAtomValue(zslFeedbackPredictionIdAtom);

  const resetToBlank = useAtomCallback(
    useCallback(
      async (_get, set) => {
        set(dismissFeedbackFormCommand);
        await clearCanvas(centerAndZoomOnNode);
      },
      [clearCanvas, centerAndZoomOnNode],
    ),
  );

  const emitImplicitBadEvent = useCallback(() => {
    emitAiPredictionUserFeedbackEvent({
      prediction_id,
      correlation_id: null,
      user_rating: IMPLICIT_BAD,
      user_feedback: null,
    });
  }, [emitAiPredictionUserFeedbackEvent, prediction_id]);

  const handleUseAi = useCallback(() => {
    // This means users have nodes set from AI, and they pressed the "Start over" button to get here
    if (hasZsl) {
      emitImplicitBadEvent();
    }

    setShowWelcomeModal(false);
    setShowAIStreamCreateModal(true);
    emitEventOnAICopilotStart();
  }, [
    hasZsl,
    setShowWelcomeModal,
    setShowAIStreamCreateModal,
    emitEventOnAICopilotStart,
    emitImplicitBadEvent,
  ]);

  const handleUseTemplate = useCallback(async () => {
    // This means users have nodes set from AI, and they pressed the "Start over" button to get here
    if (hasZsl) {
      emitImplicitBadEvent();
      await resetToBlank();
    }

    setShowWelcomeModal(false);
    setShowCanvasTemplateWidget(true);
    emitEventOnStartFromScratch();
  }, [
    hasZsl,
    setShowWelcomeModal,
    setShowCanvasTemplateWidget,
    emitEventOnStartFromScratch,
    emitImplicitBadEvent,
    resetToBlank,
  ]);

  return (
    <>
      <OnboardingDataPreloader />
      <Modal.ContentHeader textAlign="start">
        <Text type="SectionHeader">Create canvas</Text>
        <p className={styles.subTitle}>
          A canvas created from AI or a template will also create the Zapier
          assets to support the diagrammed process.
        </p>
      </Modal.ContentHeader>
      <Modal.ContentBody textAlign="start">
        <div className={styles.buttonWrappers}>
          <button
            className={classNames(styles.actionButton, styles.aiButton)}
            onClick={handleUseAi}
            onKeyDown={handleUseAi}
          >
            <div className={styles.iconWrapper}>
              <Icon name="miscAI" size={24} />
            </div>
            <div className={styles.textWrapper}>
              <div className={styles.buttonHeadingWrapper}>
                <Text type="ParagraphHeader3">Start with AI Copilot</Text>
                <Tag size="small" variant="simple">
                  Beta
                </Tag>
                <div className={styles.arrowIconWrapper}>
                  <Icon name="arrowBigRight" size={20} />
                </div>
              </div>
              <p className={styles.buttonDescription}>
                Create entire systems and assets as fast as you can type.
              </p>
            </div>
          </button>
          <button
            className={styles.actionButton}
            onClick={handleUseTemplate}
            onKeyDown={handleUseTemplate}
          >
            <div className={styles.iconWrapper}>
              <Icon name="formAdd" size={24} />
            </div>
            <div className={styles.textWrapper}>
              <div className={styles.buttonHeadingWrapper}>
                <Text type="ParagraphHeader3">
                  Start from scratch or template
                </Text>
                <div className={styles.arrowIconWrapper}>
                  <Icon name="arrowBigRight" size={20} />
                </div>
              </div>
              <p className={styles.buttonDescription}>
                Create a new blank canvas and add existing Zapier assets or
                utilize a templated workflow.
              </p>
            </div>
          </button>
        </div>
      </Modal.ContentBody>
      <Modal.ContentFooter>
        <div className={styles.footerInner}>
          <Button variant="tertiary" onPress={handleCloseModal} size="medium">
            Cancel
          </Button>
        </div>
      </Modal.ContentFooter>
    </>
  );
}

function useCenterAndZoomOnNode() {
  const { setViewport } = useReactFlow();

  const centerAndZoomOnNode = useCallback(
    (node: Node) => {
      const { position } = node || {};
      const { x, y } = position || {};
      // If selectedNode is defined, x and y should also be defined
      if (x !== undefined && y !== undefined) {
        setViewport(
          {
            x: window.innerWidth / 2 - 460 - x + 130,
            y: window.innerHeight / 2 - y - 160,
            zoom: 1,
          },
          {
            duration: 300,
          },
        );
      }
    },
    [setViewport],
  );

  return { centerAndZoomOnNode };
}
